/* Selection-level operations shared by keyboard, panel buttons, and menus. */
import { findItem, hooks, pushUndo, state } from './state.ts';
import { uid } from './utils.ts';
import { roomWalls, type Rect } from './room.ts';

/** Room tool commit: four welded walls for `rect` (centerline), selected and
    ready to drag into place. Returns false (and changes nothing) when the
    rectangle is too small to be a room. */
export function addRoom(rect: Rect): boolean {
  const walls = roomWalls(rect);
  if (!walls) return false;
  pushUndo();
  state.doc.walls.push(...walls);
  state.sel = new Set(walls.map(w => w.id));
  hooks.setTool('select');
  hooks.markDirty(); hooks.renderPanel();
  hooks.coach('room');
  return true;
}

export function deleteSel(): void {
  if (!state.sel.size) return;
  pushUndo();
  const ids = state.sel;
  state.doc.walls = state.doc.walls.filter(w => !ids.has(w.id));
  const wallIds = new Set(state.doc.walls.map(w => w.id));
  state.doc.openings = state.doc.openings.filter(o => !ids.has(o.id) && wallIds.has(o.wallId));
  state.doc.constraints = (state.doc.constraints || []).filter(c =>
    wallIds.has(c.a) && (!('b' in c) || wallIds.has(c.b)));
  state.doc.fixtures = state.doc.fixtures.filter(f => !ids.has(f.id));
  state.doc.labels = state.doc.labels.filter(l => !ids.has(l.id));
  state.sel.clear();
  hooks.markDirty(); hooks.renderPanel();
}

export function duplicateSel(): void {
  if (!state.sel.size) return;
  pushUndo();
  const newSel = new Set<string>();
  const idMap: Record<string, string> = {};
  for (const id of state.sel) {
    const r = findItem(id); if (!r) continue;
    const cp = JSON.parse(JSON.stringify(r.item));
    cp.id = uid(cp.id[0]);
    idMap[id] = cp.id;
    if (r.kind === 'walls') { cp.x1 += 12; cp.y1 += 12; cp.x2 += 12; cp.y2 += 12; }
    else if (r.kind === 'fixtures' || r.kind === 'labels') { cp.x += 12; cp.y += 12; }
    else if (r.kind === 'openings') {
      if (!state.sel.has(cp.wallId)) continue; // only clone with its wall
    }
    state.doc[r.kind].push(cp);
    newSel.add(cp.id);
  }
  for (const o of state.doc.openings) if (idMap[o.wallId] && newSel.has(o.id)) o.wallId = idMap[o.wallId];
  state.sel = newSel;
  hooks.markDirty(); hooks.renderPanel();
}

export function rotateSel(by: number): void {
  const items = [...state.sel].map(findItem).filter(r => r && r.kind === 'fixtures');
  if (state.tool === 'fixture' && state.placeGhost) {
    state.placeGhost.rot = (state.placeGhost.rot + by + 360) % 360;
    hooks.requestDraw(); return;
  }
  if (!items.length) return;
  pushUndo();
  for (const r of items) r!.item.rot = ((r!.item.rot || 0) + by + 360) % 360;
  hooks.markDirty(); hooks.renderPanel();
}

export function flipDoor(which: 'hinge' | 'swing'): void {
  const types = which === 'hinge' ? ['door', 'pocket'] : ['door', 'double', 'bifold'];
  const items = [...state.sel].map(findItem).filter(r => r && r.kind === 'openings' && types.includes(r.item.type));
  if (!items.length) return;
  pushUndo();
  for (const r of items) {
    if (which === 'hinge') r!.item.hinge = r!.item.hinge ? 0 : 1;
    else r!.item.swing = (r!.item.swing || 1) * -1;
  }
  hooks.markDirty();
}
